<script lang="ts" setup>
import type { MediaFormat } from '@autobid/strapi-integration/rest/components'
import type {
  StrapiImageFormatName,
  StrapiMedia,
} from '@autobid/strapi-integration/typescript/strapi/Strapi'
import { parseModifiers } from '@autobid/ui/providers/strapi'

interface ImageData {
  src: string
  alt: string
  srcset?: string
  width: number
  height: number
  loading?: 'lazy' | 'eager'
}

interface Modifiers {
  width?: number
  height?: number
  resize?: string
  fit?: 'cover' | 'contain' | 'fill' | 'inside' | 'outside'
  position?:
    | 'top'
    | 'right top'
    | 'right'
    | 'right bottom'
    | 'bottom'
    | 'left bottom'
    | 'left'
    | 'left top'
  trim?: number
  format?: 'jpg' | 'jpeg' | 'png' | 'webp' | 'avif' | 'gif' | 'heif'
  // 0 - 100
  quality?: number
  rotate?: number
  enlarge?: string
  flip?: 1
  flop?: 1
  sharpen?: number
  median?: number
  gamma?: number
  negate?: 1
  normalize?: 1
  threshold?: number
  tint?: number
  grayscale?: 1
  animated?: 1
  version?: string
}

interface Props {
  data: StrapiMedia
  modifiers?: Modifiers
  size?: StrapiImageFormatName
  loading?: 'lazy' | 'eager'
}

const props = defineProps<Props>()

const { isSSG } = useRuntimeConfig().public
const canBeConvertedToWebp = !['image/svg+xml', 'image/gif'].includes(
  props.data.mime,
)

const preapredModifiers: Modifiers = {
  ...(canBeConvertedToWebp ? { format: 'webp' } : {}),
  ...(props.modifiers ?? {}),
  ...(props.data.updatedAt ? { version: props.data.updatedAt } : {}),
}

const strapiDomain = useRuntimeConfig().public.autobidCMS.url

function addStrapiDomain(src: string) {
  return `${strapiDomain}${src}`
}

const imageData = (function (): ImageData {
  const getSrcSet = () => {
    const formats = [
      // append all images, nor thumbnail
      ...(Object.values(props.data.formats ?? {}) as MediaFormat[])
        .filter(el => el.url !== props.data.formats?.thumbnail?.url)
        .map(el => ({ width: el.width, url: el.url })),
      // append original image
      {
        width: props.data.width,
        url: props.data.url,
      },
    ]
      .flat()
      .map((el) => {
        return {
          ...el,
          url: `${el.url}?${parseModifiers(preapredModifiers)}`,
        }
      })
      .sort((a, b) => (Number(a.width) > Number(b.width) ? -1 : 1))

    return props.size && props.data.formats?.[props.size]
      ? undefined
      : formats
          .map(({ width, url }) => `${addStrapiDomain(url)} ${width}w`)
          .join(', ')
  }

  const { width, height, url } = props.size && props.data.formats?.[props.size]
    ? props.data.formats[props.size]
    : props.data

  return {
    ...(
      isSSG
        ? {
            src: `@strapi${url}`,
          }
        : {
            provider: 'strapi',
            src: `${addStrapiDomain(url)}?${parseModifiers({
              ...preapredModifiers,
              width,
              height,
            })}`,
            srcset: getSrcSet(),
          }),
    alt: props.data.alternativeText || '',
    width,
    height,
    loading: props.loading,
    ...preapredModifiers,
  }
}())
</script>

<template>
  <nuxt-img v-bind="imageData" />
</template>
